import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchProductivityPeriod, type BreakdownDimension } from '@apis/productivity';
import type { ProductivityGranularity } from '@interfaces/IProductivity';
import type { IProductivityRange } from '../utils/productivityRange';

/**
 * The timeframe-scoped payload — chart, confidence mix and the open tab's
 * rows — in one request. The tab is sent but NOT part of the key: a tab switch
 * must not refetch the chart. The rows that ride along are seeded into the
 * breakdown query's cache, so the Breakdown card's own hook finds them and
 * makes no request of its own. Gated on site + range so a `custom` timeframe
 * waits for both ends. Across a timeframe change the previous payload stays
 * on hand (`isPlaceholderData`) until the new one arrives.
 */
export function useProductivityPeriod(
  siteId: string | undefined,
  range: IProductivityRange | null,
  granularity: ProductivityGranularity,
  tab: BreakdownDimension,
) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['productivity', 'period', siteId, range?.dateFrom, range?.dateTo, granularity],
    queryFn: async ({ signal }) => {
      const period = await fetchProductivityPeriod(siteId!, range!, granularity, tab, signal);
      if (period.breakdown) {
        queryClient.setQueryData(
          ['productivity', 'breakdown', period.breakdown.dimension, siteId, range!.dateFrom, range!.dateTo],
          period.breakdown,
        );
      }
      return period;
    },
    enabled: !!siteId && !!range,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
